import { supabase } from "@/lib/supabase";
import { buildDemoData } from "@/utils/demo";

type Row = Record<string, unknown>;

const DEMO_TABLES = [
  "sales",
  "inventory_items",
  "advisor_movements",
  "expenses",
  "receivables",
  "loans",
  "promotions",
  "encargos",
  "restock_items",
  "notifications",
  "clients",
  "products",
  "import_logs",
  "stock_entries",
] as const;

// Borra el contenido operativo (conserva los perfiles de usuarios).
export const clearOperationalData = async (): Promise<void> => {
  for (const table of DEMO_TABLES) {
    const { error } = await supabase.from(table).delete().not("id", "is", null);
    if (error) throw new Error(error.message);
  }
};

// Siembra datos de demostración reales en la base de datos.
export const seedDemoData = async (): Promise<void> => {
  const demo = buildDemoData();

  const { data: productRows, error: productErr } = await supabase
    .from("products")
    .insert(
      demo.products.map((p) => ({
        sku: p.sku,
        name: p.name,
        category: p.category,
        pv: p.pv,
        cost_base: p.costBase,
        price_public: p.pricePublic,
      }))
    )
    .select();
  if (productErr) throw new Error(productErr.message);

  const skuToId = new Map<string, string>();
  (productRows as Row[]).forEach((r) => skuToId.set(String(r.sku), String(r.id)));

  const invRows = demo.inventory
    .map((item) => {
      const product = demo.products.find((p) => p.id === item.productId);
      const pid = product ? skuToId.get(product.sku) : undefined;
      if (!pid) return null;
      return {
        product_id: pid,
        stock_casa: item.stockCasa,
        stock_punto_clear: item.stockPuntoClear,
        stock_by_advisor: {},
      };
    })
    .filter(Boolean);
  if (invRows.length) {
    const { error } = await supabase.from("inventory_items").insert(invRows);
    if (error) throw new Error(error.message);
  }

  const { data: clientRows, error: clientErr } = await supabase
    .from("clients")
    .insert(demo.clients.map((c) => ({ name: c.name, cedula: c.cedula, phone: c.phone, address: c.address })))
    .select();
  if (clientErr) throw new Error(clientErr.message);
  const cedulaToId = new Map<string, string>();
  (clientRows as Row[]).forEach((r) => cedulaToId.set(String(r.cedula), String(r.id)));

  const saleRows = demo.sales
    .map((s) => {
      const product = demo.products.find((p) => p.id === s.productId);
      const pid = product ? skuToId.get(product.sku) : undefined;
      if (!pid) return null;
      return {
        date: s.date,
        client_id: cedulaToId.get(s.clientId) ?? null,
        client_name: s.clientName,
        product_id: pid,
        product_name: s.productName,
        qty: s.qty,
        pv: s.pv,
        total: s.total,
        margin_pct: s.marginPct,
        type: s.type,
        origin: s.origin,
        delivery: s.delivery,
      };
    })
    .filter(Boolean);
  if (saleRows.length) {
    const { error } = await supabase.from("sales").insert(saleRows);
    if (error) throw new Error(error.message);
  }

  const receivableRows = demo.receivables.map((r) => ({
    date: r.date,
    client_name: r.clientName,
    product_name: r.productName,
    initial_value: r.initialValue,
    payments: r.payments,
  }));
  if (receivableRows.length) {
    const { error } = await supabase.from("receivables").insert(receivableRows);
    if (error) throw new Error(error.message);
  }

  const loanRows = demo.loans.map((l) => ({
    date: l.date,
    concept: l.concept,
    value: l.value,
    type: l.type,
  }));
  if (loanRows.length) {
    const { error } = await supabase.from("loans").insert(loanRows);
    if (error) throw new Error(error.message);
  }

  const promoRows = demo.promotions.map((p) => ({
    title: p.title,
    description: p.description,
    discount_pct: p.discountPct,
    start_date: p.startDate,
    end_date: p.endDate,
  }));
  if (promoRows.length) {
    const { error } = await supabase.from("promotions").insert(promoRows);
    if (error) throw new Error(error.message);
  }

  const expenseRows = demo.expenses.map((e) => ({
    date: e.date,
    concept: e.concept,
    category: e.category,
    wallet: e.wallet,
    amount: e.amount,
    note: e.note,
  }));
  if (expenseRows.length) {
    const { error } = await supabase.from("expenses").insert(expenseRows);
    if (error) throw new Error(error.message);
  }
};